// src/api/axiosFun.js
import axios from "axios";
import Qs from "qs";
import store from "../vuex/store"; // 假设你使用 Vuex 来管理加载状态
import { Message } from "element-ui";
// 登录态校验与失效处理统一收口到 utils/auth.js
import { getToken, isTokenValid, handleLoginExpired } from "../utils/auth";

// 创建 axios 实例，不设置baseURL，让它使用代理配置
const instance = axios.create({});

// 请求拦截器
instance.interceptors.request.use(
	(config) => {
		config.loadingTimer = setTimeout(() => {
			store.commit("setLoading", true); // 设置加载状态为 true
		}, 2000);
		const token = getToken();
		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
		}
		return config;
	},
	(error) => {
		clearTimeout(error.config.loadingTimer); // 清除定时器
		store.commit("setLoading", false); // 设置加载状态为 false
		return Promise.reject(error);
	}
);

// 响应拦截器
instance.interceptors.response.use(
	(response) => {
		clearTimeout(response.config.loadingTimer); // 清除定时器
		store.commit("setLoading", false); // 设置加载状态为 false
		return response.data;
	},
	(error) => {
		clearTimeout(error.config.loadingTimer); // 清除定时器
		store.commit("setLoading", false); // 设置加载状态为 false
		if (error.response) {
			switch (error.response.status) {
				case 401:
				case 403:
					// 判断是否第一个调用的401 并打回登录窗口
					if (getToken()) {
						// 原实现在此调用 this.$router（模块作用域下 this 为 undefined）导致跳转失效，
						// 现统一由 utils/auth.js 清理登录缓存并跳登录页
						handleLoginExpired("登录已过期，请重新登录！");
					}
					break;
				case 404:
					Message.warning("请求的资源不存在！");
					break;
				case 429:
					Message.warning("操作频繁，请稍后再试！");
					break;
				case 500:
					Message.warning("服务器内部错误！");
					break;
				case 502:
					Message.warning("网关错误！");
					break;
				case 503:
					Message.warning("服务不可用！");
					break;
				case 504:
					Message.warning("网关超时！");
					break;
				default:
					console.error("请求错误:", error.response.status, error.response.data);
					break;
			}
		}
		return Promise.reject(error);
	}
);

// 失败 Promise 标记为「已处理」：调用方没写 catch 时也不会产生 unhandled rejection 噪音
function rejectSilently(message) {
	const promise = Promise.reject(new Error(message));
	promise.catch(() => {});
	return promise;
}

// 通用请求函数
const request = (method, url, params, config = {}) => {
	// token 有效性（JWT exp）统一在 utils/auth.js 判断
	const token = getToken();
	if (!token) {
		return rejectSilently("登录已失效，请重新登录！");
	}
	if (!isTokenValid(token)) {
		// 兜底：正常情况下路由守卫已拦下失效登录态，这里兜住「停留在页面期间 token 到期」
		handleLoginExpired();
		return rejectSilently("登录已过期，请重新登录！");
	}
	const defaultConfig = {
		method: method,
		url: url,
		headers: {},
		data: params,
		traditional: true
	};
	if (method === "get") {
		defaultConfig.params = params;
		delete defaultConfig.data;
	} else {
		defaultConfig.headers["Content-Type"] = config.contentType || "application/json";
		if (config.contentType === "application/x-www-form-urlencoded") {
			defaultConfig.transformRequest = [
				function (data) {
					return Qs.stringify(data);
				}
			];
		} else if (config.contentType === "multipart/form-data") {
			delete defaultConfig.headers["Content-Type"];
		}
	}
	// 添加时间戳
	if (params) {
		params["_t"] = new Date().toISOString(); // 将 Date 对象转换为 ISO 字符串
	}
	return instance(defaultConfig);
};

// 登录请求函数
const requestLogin = (method, url, params, config = {}) => {
	const defaultConfig = {
		method: method,
		url: url,
		headers: {},
		data: params,
		traditional: true
	};
	if (method === "get") {
		defaultConfig.params = params;
		delete defaultConfig.data;
	} else {
		defaultConfig.headers["Content-Type"] = config.contentType || "application/json";
		if (config.contentType === "application/x-www-form-urlencoded") {
			defaultConfig.transformRequest = [
				function (data) {
					return Qs.stringify(data);
				}
			];
		} else if (config.contentType === "multipart/form-data") {
			delete defaultConfig.headers["Content-Type"];
		}
	}
	// 添加时间戳
	if (params) {
		params["_t"] = new Date().toISOString(); // 将 Date 对象转换为 ISO 字符串
	}
	return instance(defaultConfig);
};
// 登录请求方法
const loginreq = (method, url, params) => {
	return requestLogin(method, url, params, { contentType: "application/json" });
};

// 通用公用方法
const req = (method, url, params) => {
	return request(method, url, params, { contentType: "application/x-www-form-urlencoded" });
};

// 通用 JSON 方法
const reqF = (method, url, params) => {
	return request(method, url, params, { contentType: "application/json" });
};

// 通用 JSON 方法（无参数）
const reqNF = (method, url, params) => {
	return request(method, url, params, { contentType: "application/x-www-form-urlencoded" });
};

// 上传附件
const uploadFile = (method, url, params) => {
	return request(method, url, params, { contentType: "multipart/form-data" });
};

// 请求文件流
const reqFile = (method, url, params) => {
	return request(method, url, params, { contentType: "application/json", responseType: "arraybuffer" });
};

export { loginreq, req, reqF, reqNF, uploadFile, reqFile };
